import React, { type FC, type ReactNode, useContext } from 'react';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import type { Styling } from 'src/context/styling';
import type { CSSInterpolation } from '@emotion/serialize';
import mq from 'src/styling/media-queries';
import { flex } from 'src/styling/constants';
import { amplitudeTrackEvent } from 'src/helpers/amplitude-event-track';
import {
  backgroundColorForRecentClubRibbon,
  createAnimatedStyles,
  isPromoEnabledOnPage,
  MOST_RECENTLY_VIEW_CLUB_ID,
} from 'src/components/layout/redesign/header-shared/promo-helper';
import { useRibbonEffects } from 'src/custom-hooks/use-ribbon-effect';
import type { PromoRibbonProps } from 'src/components/layout/redesign/header-shared/promo-ribbon';
import { TranslatedStaticText } from 'src/translators/static-text-translator';
import TimeCircle from 'src/components/icons/time-circle';
import { mqDesktopHeader } from 'src/components/layout/redesign/header-shared/header-styling';
import { getLocalStorageItem, removeLocalStorageItem } from 'src/helpers/local-storage-helper';
import { getClubInformation } from 'src/content/club-look-ups-by-id';
import ArrowRightThick from 'src/components/icons/arrow-right-thick';
import { getClubDisplayValues } from 'src/enums/build/build-type';
import { Link } from 'gatsby';
import { getAbsoluteClubPagePath, getClubPagePath } from 'src/helpers/navigation-helper';
import { getBuildDomainForBuildType } from 'src/enums/build/build-domain';

const createComponentStyling = (styling: Styling, isMobile?: boolean) =>
  ({
    wrapper: [
      flex.columnCentered,
      {
        width: '100%',
        padding: '1rem 0',
      },
    ],
    container: [
      flex.row,
      {
        width: '100%',
        maxWidth: '92.25rem',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
      },
      isMobile
        ? { padding: '0 1.25rem' }
        : mqDesktopHeader({
            padding: ['0 1.25rem', '0 1.25rem', '0 3.75rem'],
          }),
    ],
    textWrapper: [
      styling.fonts.regular,
      mq({
        display: 'flex',
        alignItems: ['flex-start', 'center'],
        maxWidth: '100%',
        color: styling.colors.capeCod,
        fontSize: '1rem',
        lineHeight: '125%',
        flexDirection: 'row',
        flexWrap: 'wrap',
      }),
    ],
    link: {
      display: 'inline',
      textDecoration: 'underline',
      color: styling.colors.camelot,
    },
    closeWrapper: [
      styling.fonts.regular,
      {
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'flex-end',
        fontSize: '1rem',
        textDecoration: 'underline',
        lineHeight: '125%',
        height: '100%',
        cursor: 'pointer',
        minWidth: '2rem',
      },
    ],
    clubNameStyle: mq({
      textTransform: 'capitalize',
    }),
    textWithArrow: mq({ whiteSpace: 'nowrap' }),
    arrowStyle: { marginLeft: '0.2rem' },
  }) satisfies Record<string, CSSInterpolation>;

type RecentlyViewedClubRibbonProps = Omit<PromoRibbonProps, 'promoRibbon'> & {
  isMobile?: boolean;
  enabled: boolean;
  setShouldShowClubRibbon: (value: boolean) => void;
};

const RecentlyViewedClubRibbon = ({
  isMobileNavOpen,
  onPointerOver,
  isMobile,
  enabled,
  setShouldShowClubRibbon,
}: RecentlyViewedClubRibbonProps) => {
  const { headerContent } = useContext(GlobalCmsContent);
  const { styling, language, buildType, basePath, pageLevel } = useContext(SiteContext);
  const {
    componentHeight,
    shouldHide,
    componentRef,
    textAndChevronContainerRef,
    textRef,
    chevronRef,
  } = useRibbonEffects(0);

  const ribbon = headerContent?.headerRecentlyViewedClubRibbon;
  const clubId = getLocalStorageItem(MOST_RECENTLY_VIEW_CLUB_ID)!;

  if (!clubId) {
    return null;
  }

  const { clubName, buildType: clubBuildType } = getClubInformation(clubId);
  const clubInformation = getClubInformation(clubId);

  if (!isPromoEnabledOnPage(ribbon, basePath, language, buildType) || !clubName) {
    return null;
  }

  const componentStyling = createComponentStyling(styling, isMobile);
  const animatedStyles = createAnimatedStyles(
    componentHeight,
    shouldHide || !enabled,
    isMobileNavOpen
  );

  const handleClick = () =>
    amplitudeTrackEvent(`Click to Recently viewed Club Ribbon`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
    });

  const onClickRemove = () => {
    removeLocalStorageItem(MOST_RECENTLY_VIEW_CLUB_ID);
    setShouldShowClubRibbon(false);
  };

  const clubNamePrefix = getClubDisplayValues(clubBuildType).clubNamePrefix;
  const fullClubName = `${clubNamePrefix} ${clubName}`.split(' ');
  const lastPartClubName = fullClubName.pop();
  const beforeLastPartClubName = fullClubName.join(' ');

  const LinkComponent: FC<{
    children: ReactNode | undefined;
    style: CSSInterpolation;
    handleClick: () => void;
  }> = ({ children, style, handleClick }) => {
    return getBuildDomainForBuildType(clubInformation.buildType) ===
      getBuildDomainForBuildType(buildType) ? (
      <Link to={getClubPagePath('', clubId, language)} css={style} onClick={handleClick}>
        {children}
      </Link>
    ) : (
      <a href={getAbsoluteClubPagePath('', clubId, language)} css={style} onClick={handleClick}>
        {children}
      </a>
    );
  };

  return (
    <div
      css={[
        animatedStyles.openCloseWrapper,
        {
          backgroundColor: backgroundColorForRecentClubRibbon(styling, ribbon?.color),
        },
      ]}
      onPointerOver={onPointerOver}
    >
      <div
        css={componentStyling.wrapper}
        ref={componentRef}
        data-testid={`recently-viewed-club-ribbon-${componentHeight}`}
      >
        <div css={componentStyling.container} ref={textAndChevronContainerRef}>
          <div css={componentStyling.textWrapper} ref={textRef}>
            <span css={{ marginRight: '0.62rem' }}>{ribbon?.text}</span>
            <LinkComponent style={componentStyling.link} handleClick={handleClick}>
              <span css={componentStyling.clubNameStyle}>{`${beforeLastPartClubName} `}</span>
              <span css={componentStyling.textWithArrow}>
                {lastPartClubName}
                <ArrowRightThick
                  height="0.8125rem"
                  fill={styling.colors.camelot}
                  size={17}
                  svgStyle={componentStyling.arrowStyle}
                />
              </span>
            </LinkComponent>
          </div>
          <div
            ref={chevronRef}
            tabIndex={0}
            css={componentStyling.closeWrapper}
            onClick={onClickRemove}
          >
            {!isMobile && (
              <span css={{ marginRight: '0.62rem' }}>
                <TranslatedStaticText lookupKey="remove" />
              </span>
            )}
            <TimeCircle fill={styling.colors.capeCod} size="1rem" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentlyViewedClubRibbon;
